<template lang="pug">
v-dialog(v-model="dialog" persistent max-width="400px" :fullscreen="$vuetify.breakpoint.xsOnly")
  v-card.d-flex.flex-column(:style="$vuetify.breakpoint.xsOnly ? 'height: 100%;' : ''")
    v-card-title.pa-0.pt-3.pr-3
      v-toolbar(dense flat)
        v-spacer
        v-btn(v-if="!errorTitle && !success" icon :disabled="loading" @click="dialog = false")
          v-icon mdi-close

    //- Возврат не прошёл: отклонён, истекло время или ошибка сервиса
    template(v-if="errorTitle")
      v-container.d-flex.flex-column.flex-grow-1.justify-center.text-center
        div
          v-icon(size="80" color="error") mdi-close-circle-outline
          .text-h5.font-weight-bold.mt-6 {{ errorTitle }}
          .subtitle-1.mt-2.text--secondary(v-if="errorText") {{ errorText }}
      v-card-actions.pa-4.flex-column
        v-btn.text-h5.font-weight-bold(block color="primary" @click="editBillNumber()") {{ $t("изменить_номер_операции") }}
        v-btn.text-h5.mt-2(block text color="grey" @click="dialog = false") {{ $t("закрыть") }}

    v-container.d-flex.flex-column.flex-grow-1.justify-center(v-else style="overflow-y: auto;")
      //- Оплата подтверждена - показываем галочку, потом уходим фискализировать чек
      template(v-if="success")
        .text-center.mb-16
          svg.re-success-check(viewBox="0 0 52 52")
            circle.re-success-check__circle(cx="26" cy="26" r="24" fill="none")
            path.re-success-check__mark(fill="none" d="M14 27l8 8 16-16")
          .text-h5.font-weight-bold.mt-8 {{ $t("возврат_выполнен") }}
          .re-number-font.text-h4.mt-2 {{ amount | numeral('0,0.[00]') }} ₸

      //- Создание возвратного QR
      template(v-else-if="loading")
        .text-center.mb-16
          v-progress-circular(indeterminate color="secondary" size="64")
          .subtitle-1.mt-6 {{ $t("создание_qr_кода") }}...

      //- QR отсканирован - банк обрабатывает возврат
      template(v-else-if="processingRefund")
        .text-center.mb-16
          v-progress-circular(indeterminate color="primary" size="64" width="4")
          .text-h5.font-weight-bold.mt-8 {{ $t("обработка_возврата") }}
          .subtitle-1.mt-2.text--secondary {{ $t("дождитесь_завершения_операции") }}

      //- Возвратный QR для сканирования покупателем
      template(v-else-if="qrcode")
        //- Смещаем блок чуть выше центра
        .mb-16
          v-row
            v-col
              .re-number-font.text-h1.text-center {{ amount | numeral('0,0.[00]') }} ₸

          v-row.mt-4
            v-col
              v-row(justify="center")
                img.halyk-qrcode-img(src="/static/halyk-logo.png" height="26" width="26")
                div.halyk-corners
                  div.halyk-qrcode
                    qrcode(:value="qrcode" size="180" level="H" renderAs="svg")

          v-row.mt-6
            v-col.px-6
              .subtitle-1.text-center {{ $t("покупатель_должен_отсканировать_qr_банком_которым_оплачивал") }}

</template>
<script>
import { mapActions } from 'vuex'
import qrcode from 'qrcode.vue'

const POLL_INTERVAL = 1500
// Сколько ждём, пока покупатель отсканирует возвратный QR
const SCAN_TIMEOUT = 180000
// Сколько ждём обработку возврата банком после сканирования
const CONFIRM_TIMEOUT = 120000
// Сколько держим экран успеха перед фискализацией
const SUCCESS_DURATION = 2000

export default {
  components: {
    qrcode,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    amount: {
      type: Number,
      required: true,
    },
    billNumber: {
      type: [String, Number],
      default: null,
    },
  },

  data: () => ({
    loading: false,
    errorTitle: null,
    errorText: null,
    qrcode: null,
    refundId: null,
    refundBillNumber: null,
    phase: null,
    qrStatus: null,
    scanned: false,
    confirmSent: false,
    statusInterval: null,
    qrShownAt: null,
    success: false,
    successTimeout: null,
    pollStartedAt: null,
    statusLoading: false,
  }),

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },

    // Покупатель отсканировал возвратный QR - банк обрабатывает возврат
    processingRefund() {
      return this.confirmSent || this.phase === 'REFUND' || this.qrStatus === 'SCANNED'
    },
  },

  watch: {
    value(opened) {
      if (opened) {
        this.start()
      } else {
        this.reset()
      }
    },
  },

  beforeDestroy() {
    this.stopPolling()
    this.clearSuccessTimeout()
  },

  methods: {
    ...mapActions({
      refundInit: 'halykQr/refundInit',
      getRefundStatus: 'halykQr/getRefundStatus',
      confirmRefund: 'halykQr/confirmRefund',
      analyticsLogEvent: 'analytics/logEvent',
    }),

    // Событие воронки Единого QR: сумма всегда, длительность - от момента показа QR
    logQrEvent(eventName, eventProperties) {
      const properties = { amount: this.amount, ...eventProperties }
      if (this.qrShownAt) {
        properties.duration_sec = Math.round((Date.now() - this.qrShownAt) / 1000)
      }
      this.analyticsLogEvent({ eventName, eventProperties: properties })
    },

    reset() {
      this.stopPolling()
      this.clearSuccessTimeout()
      this.success = false
      this.loading = false
      this.errorTitle = null
      this.errorText = null
      this.qrcode = null
      this.refundId = null
      this.refundBillNumber = null
      this.phase = null
      this.qrStatus = null
      this.scanned = false
      this.confirmSent = false
      this.pollStartedAt = null
      this.statusLoading = false
      this.qrShownAt = null
    },

    async start() {
      this.reset()
      this.loading = true
      this.logQrEvent('re_halyk_qr_refund_started')
      try {
        const { data } = await this.refundInit({
          billNumber: Number(this.billNumber),
          amount: this.amount,
        })
        this.refundId = data.refundId
        this.qrcode = data.qrcode
        this.phase = 'REFUND_QR'
        this.qrStatus = 'NEW'
        this.loading = false
        this.qrShownAt = Date.now()
        this.logQrEvent('re_halyk_qr_refund_code_shown')
        this.startPolling()
      } catch (error) {
        this.loading = false
        this.setError(error)
        this.logQrEvent('re_halyk_qr_refund_failed', { stage: 'INIT', error_code: this.errorCode(error) })
      }
    },

    // Даём кассиру увидеть подтверждение, и только потом отдаём чек на фискализацию
    showSuccess(done) {
      this.success = true
      this.successTimeout = setTimeout(() => {
        this.successTimeout = null
        done()
        this.dialog = false
      }, SUCCESS_DURATION)
    },

    startPolling() {
      this.pollStartedAt = Date.now()
      this.statusInterval = setInterval(() => this.pollStatus(), POLL_INTERVAL)
    },

    clearSuccessTimeout() {
      if (this.successTimeout) {
        clearTimeout(this.successTimeout)
        this.successTimeout = null
      }
    },

    stopPolling() {
      if (this.statusInterval) {
        clearInterval(this.statusInterval)
        this.statusInterval = null
      }
    },

    async pollStatus() {
      if (this.statusLoading || !this.refundId) {
        return
      }

      if (this.pollStartedAt && Date.now() - this.pollStartedAt > (this.scanned ? CONFIRM_TIMEOUT : SCAN_TIMEOUT)) {
        this.stopPolling()
        this.errorTitle = this.$t('возврат_отклонен')
        this.errorText = this.confirmSent ? this.$t('проверьте_статус_возврата_в_приложении_банка') : this.$t('покупатель_не_отсканировал_qr')
        this.logQrEvent('re_halyk_qr_refund_timeout', { stage: this.scanned ? 'REFUND' : 'SCAN' })
        return
      }

      this.statusLoading = true
      try {
        const { data } = await this.getRefundStatus({ refundId: this.refundId })
        this.phase = data.phase
        this.qrStatus = data.qrStatus

        // QR отсканирован - на обработку возврата банком даём отдельное время
        if (this.processingRefund && !this.scanned) {
          this.scanned = true
          this.pollStartedAt = Date.now()
        }

        if (data.phase === 'REFUND_QR') {
          // Покупатель отсканировал возвратный QR - подтверждаем возврат автоматически
          if (data.readyToConfirm && !this.confirmSent) {
            this.confirmSent = true
            await this.doConfirm()
          }
        } else if (data.phase === 'REFUND') {
          if (data.status === 'PAID') {
            this.stopPolling()
            this.logQrEvent('re_halyk_qr_refund_paid')
            this.showSuccess(() => this.$emit('refunded', { ...data, refundBillNumber: this.refundBillNumber }))
          } else if (data.status === 'REJECTED') {
            this.stopPolling()
            this.errorTitle = this.$t('возврат_отклонен')
            this.errorText = data.errDescription || null
            this.logQrEvent('re_halyk_qr_refund_rejected')
          }
        }
      } catch (error) {
        // Не прерываем опрос на временных ошибках статуса
        console.debug(error)
      } finally {
        this.statusLoading = false
      }
    },

    async doConfirm() {
      try {
        const { data } = await this.confirmRefund({ refundId: this.refundId })
        this.refundBillNumber = data.refundBillNumber
      } catch (error) {
        const code = error?.response?.data?.error?.code
        if (code === 'REFUND_QR_NOT_SCANNED') {
          // Сервер перепроверил у банка - скана ещё нет, ждём дальше
          this.confirmSent = false
        } else if (code === 'REFUND_ALREADY_SUBMITTED') {
          // Возврат уже отправлен (защита от дабл-клика) - просто ждём финальный статус
        } else {
          this.stopPolling()
          this.setError(error)
          this.logQrEvent('re_halyk_qr_refund_failed', { stage: 'CONFIRM', error_code: this.errorCode(error) })
        }
      }
    },

    editBillNumber() {
      this.dialog = false
      this.$emit('edit-bill-number')
    },

    // Код ошибки сервиса: для аналитики и для выбора текста
    errorCode(error) {
      const err = error && error.response && error.response.data && error.response.data.error
      return (err && err.code) || 'UNKNOWN'
    },

    // Известную ошибку показываем как есть (с описанием от банка, если оно пришло),
    // неизвестную (сеть, 5xx) - как недоступность сервиса
    setError(error) {
      const err = error && error.response && error.response.data && error.response.data.error
      if (err && err.code && this.$te(`halykQr/${err.code}`)) {
        this.errorTitle = this.$t(`halykQr/${err.code}`)
        this.errorText = err.errDescription || null
      } else {
        this.errorTitle = this.$t('сервис_единого_qr_недоступен')
        this.errorText = this.$t('пожалуйста_попробуйте_позже_еще_раз')
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.re-success-check
  width 96px
  height 96px
  stroke var(--v-success-base)
  stroke-width 3
  stroke-linecap round
  stroke-linejoin round
  animation re-success-pulse 1.6s ease-in-out 0.9s infinite

.re-success-check__circle
  stroke-dasharray 151
  stroke-dashoffset 151
  animation re-success-draw 0.6s ease-out forwards

.re-success-check__mark
  stroke-dasharray 36
  stroke-dashoffset 36
  animation re-success-draw 0.35s ease-out 0.5s forwards

@keyframes re-success-draw
  to
    stroke-dashoffset 0

@keyframes re-success-pulse
  0%, 100%
    transform scale(1)
  50%
    transform scale(1.04)

.halyk-qrcode-img
  position absolute
  margin-top 95px

.halyk-qrcode
  display inline-block
  padding 8px 8px 1px 8px
  background #fffffe
  background-image linear-gradient(#fffffe, #fffffe)

.halyk-corners
  display inline-table
  padding 18px
  background:
    linear-gradient(to right, #14a85d 4px, transparent 4px) 0 0,
    linear-gradient(to right, #14a85d 4px, transparent 4px) 0 100%,
    linear-gradient(to left, #14a85d 4px, transparent 4px) 100% 0,
    linear-gradient(to left, #14a85d 4px, transparent 4px) 100% 100%,
    linear-gradient(to bottom, #14a85d 4px, transparent 4px) 0 0,
    linear-gradient(to bottom, #14a85d 4px, transparent 4px) 100% 0,
    linear-gradient(to top, #14a85d 4px, transparent 4px) 0 100%,
    linear-gradient(to top, #14a85d 4px, transparent 4px) 100% 100%
  background-repeat no-repeat
  background-size 30px 30px
</style>
